import { useState, useEffect } from 'react';
import { logger } from '../../utils/logger';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { CampaignCard, CampaignForm, CampaignPreview, AUDIENCE_TYPES } from './campaigns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminEmailCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [sending, setSending] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    audience_type: 'all',
  });

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      logger.error('Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleAudienceChange = async (value) => {
    setFormData({ ...formData, audience_type: value });
    try {
      const response = await axios.get(`${API_URL}/admin/audience/preview?audience_type=${value}`);
      setAudiencePreview(response.data);
    } catch (error) {
      logger.error('Failed to preview audience:', error);
    }
  };

  const handleCreateCampaign = async () => {
    if (!formData.name || !formData.subject || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingCampaign) {
        await axios.put(`${API_URL}/admin/campaigns/${editingCampaign.id}`, formData);
        toast.success('Campaign updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/campaigns`, formData);
        toast.success('Campaign created successfully');
      }
      setShowCreateModal(false);
      setEditingCampaign(null);
      setFormData({ name: '', subject: '', content: '', audience_type: 'all' });
      setAudiencePreview(null);
      fetchCampaigns();
    } catch (error) {
      logger.error('Failed to save campaign:', error);
      toast.error('Failed to save campaign');
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      subject: campaign.subject,
      content: campaign.content,
      audience_type: campaign.audience_type,
    });
    setShowCreateModal(true);
    handleAudienceChange(campaign.audience_type);
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await axios.delete(`${API_URL}/admin/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      logger.error('Failed to delete campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handlePreviewCampaign = async (campaign) => {
    try {
      const response = await axios.get(`${API_URL}/admin/campaigns/${campaign.id}/preview`);
      setPreviewData({ campaign, ...response.data });
      setShowPreviewModal(true);
    } catch (error) {
      logger.error('Failed to preview campaign:', error);
      toast.error('Failed to preview campaign');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this campaign? This cannot be undone.')) return;

    try {
      setSending(true);
      const response = await axios.post(`${API_URL}/admin/campaigns/${campaignId}/send`);
      toast.success(`Campaign sent! ${response.data.sent} emails delivered, ${response.data.failed} failed.`);
      setShowPreviewModal(false);
      fetchCampaigns();
    } catch (error) {
      logger.error('Failed to send campaign:', error);
      toast.error('Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !formData.subject || !formData.content) {
      toast.error('Please fill in subject, content, and test email');
      return;
    }

    try {
      await axios.post(`${API_URL}/admin/campaigns/test`, null, {
        params: {
          subject: formData.subject,
          content: formData.content,
          test_email: testEmail,
        }
      });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      logger.error('Failed to send test email:', error);
      toast.error('Failed to send test email');
    }
  };

  const openNewCampaignModal = () => {
    setEditingCampaign(null);
    setFormData({ name: '', subject: '', content: '', audience_type: 'all' });
    setAudiencePreview(null);
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-email-campaigns">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-cyan-400" />
            Email Campaigns
          </h2>
          <p className="text-gray-400 mt-1">Send targeted emails to your fans</p>
        </div>
        <Button 
          onClick={openNewCampaignModal}
          className="bg-cyan-500 hover:bg-cyan-600"
          data-testid="new-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
          <Mail className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Campaigns Yet</h3>
          <p className="text-gray-400 mb-6">Create your first email campaign to engage with your fans.</p>
          <Button 
            onClick={openNewCampaignModal}
            className="bg-cyan-500 hover:bg-cyan-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onEdit={handleEditCampaign}
              onPreview={handlePreviewCampaign}
              onDelete={handleDeleteCampaign}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Campaign Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}</DialogTitle>
            <DialogDescription>
              Compose your email and select your target audience.
            </DialogDescription>
          </DialogHeader>
          <CampaignForm
            formData={formData}
            setFormData={setFormData}
            audiencePreview={audiencePreview}
            onAudienceChange={handleAudienceChange}
            testEmail={testEmail}
            setTestEmail={setTestEmail}
            onSendTest={handleSendTest}
            onSubmit={handleCreateCampaign}
            onCancel={() => setShowCreateModal(false)}
            isEditing={!!editingCampaign}
          />
        </DialogContent>
      </Dialog>

      {/* Preview & Send Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Campaign Preview</DialogTitle>
            <DialogDescription>
              Review your campaign before sending.
            </DialogDescription>
          </DialogHeader>
          <CampaignPreview
            previewData={previewData}
            sending={sending}
            onSend={handleSendCampaign}
            onClose={() => setShowPreviewModal(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
